import { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ReactModal from 'react-modal';
import { toast } from 'react-toastify';
import { Spinner } from '../../../../components/Spinner';
import { modalStyle } from '../../../../utils/modal';
import { useAuth } from '../../../../hooks/useAuth';
import { useEnv } from '../../../../context/env-context';
import { makeRequest } from '../../../../utils/makeRequest';

function FundingRepaymentModal({
  isOpen,
  topup,
  consumer,
  consumerStatus,
  prediction,
  close,
  funding,
  getFunding,
}) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginFunding, apiOriginConsumer } = useEnv();

  const [amount, setAmount] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [spinner, setSpinner] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);

  const achEnabled = consumer && consumer.account && consumer.account.dwollaFundingSourceUrl;
  const cardEnabled = consumerStatus?.mainCardUsage?.includes('PULL');

  useEffect(() => {
    if (achEnabled || cardEnabled) {
      if (achEnabled && cardEnabled) {
        setPaymentMethod(consumer.defaultPaymentMethod);
      } else if (achEnabled) {
        setPaymentMethod('ACH');
      } else {
        setPaymentMethod('CARD');
      }
    }
  }, [achEnabled, cardEnabled, consumer]);

  const closeModal = () => {
    close();
    setAmount('');
    setMfaCode('');
  };

  const repayFunding = async repayFundingId => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/manual_repay`,
      method: 'POST',
      data: JSON.stringify({
        fundingId: repayFundingId,
        amount: parseInt(amount, 10),
        paymentMethod,
      }),
    };

    try {
      const response = await makeRequest(config);

      if (response.status === 'CREATED') {
        toast.success('Payment initiated');
      }

      if (response.status === 'COMPLETED') {
        toast.success('Payment completed successfully');
      }

      if (response.status === 'FAILED') {
        toast.error(`Payment failed! Reason: ${response.failureReason}`);
      }

      getFunding(repayFundingId, true);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const createManualTopup = async fundingId => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/create_manual`,
      method: 'POST',
      data: JSON.stringify({
        cosnumerId: consumer.id,
        offerId: prediction.activeOffer ? prediction.activeOffer.offerId : null,
        requestedAmount: parseInt(amount, 10),
        textualReason: '',
        type: 'TOPUP',
        topupFundingId: null,
        mfaCode,
        consent: true,
      }),
    };

    try {
      await makeRequest(config);

      toast.success('Top-up created successfully');
      getFunding(fundingId);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const sendMfa = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/mfa/send`,
      method: 'POST',
      data: JSON.stringify({ consumerId: consumer.id, mfaUseCase: 'ADVANCE' }),
    };

    try {
      const response = await makeRequest(config);

      toast.success(`MFA code has been sent by ${response.mfaChannel} to ${response.mfaMask}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const handleTopup = async () => {
    if (!mfaCode) {
      toast.error('MFA code is required');

      return;
    }

    if (!amount) {
      toast.error('Amount is required');

      return;
    }

    setSpinner(true);
    await createManualTopup(funding.id);
    setSpinner(false);
    closeModal();
  };

  const handleRepay = async () => {
    if (!amount) {
      toast.error('Amount is required');

      return;
    }

    if (amount > funding.currentAmount) {
      toast.error('Amount is too high');

      return;
    }

    setSpinner(true);
    await repayFunding(funding.id);
    setSpinner(false);
    closeModal();
  };

  return (
    <ReactModal
      isOpen={isOpen}
      style={modalStyle({ width: '440px' })}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      onRequestClose={closeModal}
    >
      <Spinner visible={spinner} text="">
        <h5>{topup ? 'Manual Top-up' : 'Repay Advance'}</h5>
        <Row className="mb-2 mt-4 align-items-center">
          <Col md={topup ? 4 : 5} className="bo-same-line">
            <Form.Label>Amount:</Form.Label>
          </Col>
          <Col md={4} className="bo-same-line">
            {topup && prediction && prediction.activeOffer ? (
              <Form.Select
                className="bo-filter"
                name="amount"
                id="amount"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              >
                {prediction.activeOffer.allAmounts.map(amt => (
                  <option key={amt} value={amt}>
                    {amt}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                className="bo-input"
                type="text"
                name="amount"
                id="amount"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
            )}
          </Col>
        </Row>
        {!topup && (
          <Row className="mb-2 mt-3 align-items-start">
            <Col md={5} className="bo-same-line">
              <Form.Label>Payment Method:</Form.Label>
            </Col>
            <Col md={7} className="bo-same-line justify-content-between">
              <div className="d-flex flex-column">
                <div className="d-flex">
                  <Form.Check
                    name="radio"
                    type="radio"
                    value="ACH"
                    checked={paymentMethod === 'ACH'}
                    disabled={!achEnabled}
                    onChange={() => setPaymentMethod('ACH')}
                  />
                  <Form.Label check>ACH</Form.Label>
                </div>
                <div className="d-flex">
                  <Form.Check
                    name="radio"
                    type="radio"
                    value="CARD"
                    checked={paymentMethod === 'CARD'}
                    disabled={!cardEnabled}
                    onChange={() => setPaymentMethod('CARD')}
                  />
                  <Form.Label check>CARD</Form.Label>
                </div>
              </div>
            </Col>
          </Row>
        )}
        {topup && (
          <Row className="mb-2 mb-2 align-items-center">
            <Col md={4} className="bo-same-line">
              <Form.Label>MFA Code:</Form.Label>
            </Col>
            <Col md={4} className="bo-same-line">
              <Form.Control
                className="bo-input"
                type="text"
                name="mfaCode"
                id="mfaCode"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
              />
            </Col>
            <Col md={4} className="bo-same-line">
              <Button className="bo-link" onClick={() => sendMfa()}>
                Send MFA
              </Button>
            </Col>
          </Row>
        )}
        <Row>
          <Col className="d-flex justify-content-center gap-3 mt-5">
            <Button
              className="bo-button"
              onClick={topup ? handleTopup : handleRepay}
              disabled={!topup && !cardEnabled && !achEnabled}
            >
              {topup ? 'Create ' : 'Repay'}
            </Button>
            <Button className="bo-button" onClick={closeModal}>
              Close
            </Button>
          </Col>
        </Row>
      </Spinner>
    </ReactModal>
  );
}

export default FundingRepaymentModal;
